import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CapsuleButton, ContentSpace, MainPageWithButtons } from "../_components";
import { Selectors } from "../_reducers/app.reducer";
import { ClockinActions } from "../_stores/ClockinStore/ClockinActions";
import { IsoStartEndDatePicker } from "../_components/IsoStartEndDatePicker";
import moment from "moment";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import CustomToolbar from "./ClockinsCalendarToolbar";
import { CustomModal } from "../_components/CustomModal";
import { TimeHelper } from '../_helpers';


export function ClockinsWorkCalendarModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const locationUid = useSelector(Selectors.getSelectedLocationUid);
    const workingCalendarEntries = useSelector(Selectors.getClockinsWorkingCalendarEntries);

    const localizer = momentLocalizer(moment);

    //LOCAL STATE
    const [begin, setBegin] = useState(null);
    const [end, setEnd] = useState(null);
    const [events, setEvents] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [eventBegin, setEventBegin] = useState(null);
    const [eventEnd, setEventEnd] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const [errorMessage, setErrorMessage] = useState('');

    const showErrorMessage = (message) => {
        setErrorMessage(message);
        setTimeout(() => setErrorMessage(''), 4000);
    }

    function setBeginEndCalendar(dt) {
        let beginDate = TimeHelper.getFirstDayOfCalendarMonth(dt);
        let endDate = TimeHelper.getLastDayOfCalendarMonth(dt);

        setBegin(beginDate.format('YYYY-MM-DD'));
        setEnd(endDate.format('YYYY-MM-DD'));
    }

    //INITIALIZATION
    useEffect(() => {
        setBeginEndCalendar(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationUid]); // No se puede poner "users" para arreglar warning porque crea un bucle 

    useEffect(() => {
        if (showModal)
            return;
        dispatch(ClockinActions.listWorkingCalendarEntries(locationUid, begin, end));
     }, [dispatch, begin, end, locationUid, showModal]);

     useEffect(() => {
        let eventList = [];
        workingCalendarEntries.forEach((v) => {
            let title = t("mod_clockins_absence_type_public_holiday")
            eventList.push({
                id: v.uid,
                title,
                start: v.begin,
                end: v.end,
            });
        });
        setEvents(eventList);
     }, [dispatch, workingCalendarEntries, t]);

    //ACTION HANDLERS
    function createEvent(slotInfo) {

        slotInfo.id = moment().format('x');  //Generate id with Unix Millisecond Timestamp
        slotInfo.title = null;
        slotInfo.location = null;
        
        const dateBegin = moment(slotInfo.start).format('YYYY-MM-DD');
        const dateEnd = moment(slotInfo.end).subtract(1, 'day').format('YYYY-MM-DD');
        setEventBegin(dateBegin);
        setEventEnd(dateEnd);
        setShowModal(true);
    }

    function showEvent(slotInfo) {
        setSelectedEvent(slotInfo.id);
        setEventBegin(slotInfo.start);
        setEventEnd(slotInfo.end);
        setShowModal(true);
    }

    const addHoliday = () => {
        if (eventBegin > eventEnd) {
            showErrorMessage(t("error_600_begin_greater_than_end"));
            return;
        } else if (selectedEvent != null) {
            setEventBegin(null);
            setEventEnd(null);
            setShowModal(false);
        } else {
            dispatch(request);
        }

        async function request() {
            await dispatch(ClockinActions.addWorkingCalendarEntry(eventBegin, eventEnd));
            dispatch(ClockinActions.listWorkingCalendarEntries(locationUid, begin, end));
            setEventBegin(null);
            setEventEnd(null);
            setShowModal(false);
        }
    };

    const deleteHoliday = () => {
        
        dispatch(asyncAction);

        async function asyncAction() {
            await dispatch(ClockinActions.deleteWorkingCalendarEntry(selectedEvent));

            dispatch(ClockinActions.listWorkingCalendarEntries(locationUid, begin, end));

            setEventBegin(null);
            setEventEnd(null);
            setSelectedEvent(null);
            setShowModal(false);
        }
    };

    const handleCalendarNavigate = (date) => {
        setBeginEndCalendar(date);
    };

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("mod_clockin_work_calendar")}
                    content={content()}
                    size={'LARGE'}
                />
            }
        />
    );

    function content() {
        return (
        <>
            <div className="mt-2" />
            <div className="calendar-container">
                <Calendar
                    popup
                    selectable
                    localizer={localizer}
                    views={['month']}
                    style={{height: 600}}
                    components={{toolbar: CustomToolbar}}
                    eventPropGetter={event => {
                        let background = '#DE6987';
                        return {
                            style: {
                                backgroundColor: background
                            }
                        };
                    }}
                    onSelectEvent={(slotInfo) => showEvent(slotInfo)}
                    onSelectSlot={(slotInfo) => createEvent(slotInfo)}
                    onNavigate={handleCalendarNavigate}
                    events={events}
                    />
                <CustomModal
                    show={showModal}
                    centered
                    title={(selectedEvent != null) ? t("global_edit"):t("mod_clockins_add_public_holiday")}
                    onHide={() => setShowModal(false)}
                    content={
                        <div>                            
                            <div className="mb-2"/>
                            <IsoStartEndDatePicker
                                handleStartDateChange={(selectedBegin) => {
                                    let dateBegin = new Date(selectedBegin);
                                    setEventBegin(moment(dateBegin).format('YYYY-MM-DD'));
                                }}
                                handleEndDateChange={(selectedEnd) => {
                                    let dateEnd = new Date(selectedEnd);
                                    setEventEnd(moment(dateEnd).format('YYYY-MM-DD'));
                                }}
                                startIsoDate={eventBegin}
                                endIsoDate={eventEnd}
                                dateOnly={true}
                                isEnabled={selectedEvent == null}
                            />
                            {errorMessage ? (
                                <div className="text-danger pl-1 pt-1">
                                    {errorMessage}
                                </div>
                            ) : (
                                <div></div>
                            )}
                            <div className="mb-2"/>
                        </div>
                    }
                    footer={
                        <>
                            {selectedEvent != null && 
                                <CapsuleButton
                                    style="DANGER"
                                    text={t("global_delete")}
                                    onClick={() => {
                                        deleteHoliday();
                                    }}
                                    />
                            }
                            <CapsuleButton
                                style="DANGER"
                                text={t("global_cancel")}
                                onClick={() => {
                                    setEventBegin(null);
                                    setEventEnd(null);
                                    setSelectedEvent(null);
                                    setShowModal(false);
                                }}
                                />
                            {selectedEvent == null &&
                                <CapsuleButton
                                    isEnabled={true}
                                    text={t("global_save")}
                                    onClick={() => {
                                        addHoliday();
                                    }}
                                />
                            }
                        </>
                    }
                />
            </div>
        </>);
    }
}